/* eslint-disable */
/* This file has the linter disabled.  But it is basically a cut'n'paste from
 * the origin react-admin library with a few minor changes.
 * And there is no time left to fix it for our linting
 * */
import * as React from 'react'
import { useSearchParams } from 'react-router-dom'
import { styled } from '@mui/material/styles'
import PropTypes from 'prop-types'
import {
  Button,
  CircularProgress,
  Dialog,
  DialogTitle,
  DialogContent,
} from '@mui/material'
import {
  Form,
  required,
  useLogin,
  useNotify,
  useSafeSetState,
  useTranslate,
} from 'ra-core'
import { TextInput, useDataProvider } from 'react-admin'
import { yupResolver } from '@hookform/resolvers/yup'
import * as yup from 'yup'
import { ArrowLeftIcon } from '@heroicons/react/24/outline'
import { Tagline } from './Tagline'
import { useTranslation } from 'react-i18next'

const schema = yup
  .object()
  .shape({
    password: yup
      .string()
      .min(8, 'Password must be 8 characters long')
      .matches(/[0-9]/, 'Password requires a number')
      .matches(/[a-z]/, 'Password requires a lowercase letter')
      .matches(/[A-Z]/, 'Password requires an uppercase letter')
      .matches(/[^\w]/, 'Password requires a symbol character')
      .required(),
    confirm_password: yup
      .string()
      .oneOf([yup.ref('password'), null], 'Passwords must match'),
  })
  .required()

export const LoginForm = (props: LoginFormProps) => {
  const { redirectTo, className } = props
  const [loading, setLoading] = useSafeSetState(false)
  const { i18n } = useTranslation()
  const [searchParams, setSearchParams] = useSearchParams()

  const [action, setAction] = useSafeSetState('login')
  const [token, setToken] = useSafeSetState('')
  const [inviteMode, setInviteMode] = useSafeSetState(false)
  const [showRedirectModal, setShowRedirectModal] = useSafeSetState(false)
  const [showExpiredModal, setShowExpiredModal] = useSafeSetState(false)

  const login = useLogin()
  const translate = useTranslate()
  const notify = useNotify()
  const dp = useDataProvider()

  React.useEffect(() => {
    const qtoken = searchParams.get('token')
    const mode = searchParams.get('mode')

    if (mode === 'set') {
      setInviteMode(true)
    } else {
      setInviteMode(false)
    }

    if (qtoken !== null) {
      // Clear all client-side state to ensure reset uses only server-side token lookup
      try {
        localStorage.clear()
      } catch (e) {
        console.warn('Failed to clear localStorage during reset flow', e)
      }
      try {
        sessionStorage.clear()
      } catch (e) {
        console.warn('Failed to clear sessionStorage during reset flow', e)
      }
      try {
        // Best-effort: clear non-HttpOnly cookies accessible to JS
        document.cookie
          .split(';')
          .map((c) => c.trim())
          .filter((c) => c.length > 0)
          .forEach((c) => {
            const eqPos = c.indexOf('=')
            const name = eqPos > -1 ? c.substring(0, eqPos) : c
            // expire the cookie for current path and root path
            document.cookie = `${name}=; Max-Age=0; path=/`
          })
      } catch (e) {
        console.warn('Failed to clear cookies during reset flow', e)
      }

      setAction('reset')
      setToken(qtoken)

      // Pre-validate the token to block reused/expired links before showing the form
      const validate = async () => {
        try {
          if (mode === 'set') {
            await dp.validateInviteToken(qtoken)
          } else {
            await dp.validateResetToken(qtoken)
          }
        } catch (e) {
          // Show expired modal and redirect after 2 seconds
          setShowExpiredModal(true)
          setTimeout(() => {
            window.location.replace('/admin/login')
          }, 2000)
        }
      }
      validate()
    }
  }, [searchParams])

  const submit = (values) => {
    const logLoginAttempt = (isSuccess, userId = null) => {
      dp.create('login_attempts', {
        data: {
          user_id: userId,
          username: values.username,
          success: isSuccess,
          timestamp: new Date(),
        },
      }).catch((error) => {
        console.error('Failed to record login attempt:', error)
      })
    }

    setLoading(true)

    if (action === 'login') {
      login(values, redirectTo)
        .then((response) => {
          // Check if the login response indicates 2FA is required
          // The response is undefined if the login was successful but 2FA is required
          // In that case, we don't want to call getProfile() as it will trigger unnecessary API calls
          if (response === undefined) {
            // 2FA is likely required, don't call getProfile()
            setLoading(false)
            return
          }

          dp.getProfile()
            .then(({ data }) => {
              logLoginAttempt(true, data.id)
              i18n.changeLanguage(data.lang || 'en')
              setLoading(false)
            })
            .catch((error) => {
              // If we get a 2FA error, we don't want to show an error message
              // The redirect will be handled by the httpClient
              if (
                error &&
                error.message &&
                (error.message === '2FA setup required' ||
                  error.message === '2FA verification required' ||
                  error.message === 'Skipping API call during 2FA verification')
              ) {
                console.log('2FA required during login flow:', error.message)
                setLoading(false)
              } else {
                // For other errors, show the error message
                setLoading(false)
                notify(
                  typeof error === 'string'
                    ? error
                    : typeof error === 'undefined' || !error.message
                    ? 'Error getting user profile'
                    : error.message,
                  {
                    type: 'warning',
                  }
                )
              }
            })
        })
        .catch((error) => {
          logLoginAttempt(false)
          setLoading(false)
          notify(
            typeof error === 'string'
              ? error
              : typeof error === 'undefined' || !error.message
              ? 'ra.auth.sign_in_error'
              : error.message,
            {
              type: 'warning',
              messageArgs: {
                _:
                  typeof error === 'string'
                    ? error
                    : error && error.message
                    ? error.message
                    : undefined,
              },
            }
          )
        })
    }

    if (action === 'forgotten') {
      dp.forgottenPassword({ email: values.username })
        .then(() => {
          setLoading(false)
          notify(
            'Forgotten password request sent.  If your email address is registered you should receive an email',
            {
              type: 'success',
            }
          )
          setAction('login')
        })
        .catch((error) => {
          setLoading(false)
          notify(
            typeof error === 'string'
              ? error
              : typeof error === 'undefined' || !error.message
              ? 'ra.auth.sign_in_error'
              : error.message,
            {
              type: 'warning',
              messageArgs: {
                _:
                  typeof error === 'string'
                    ? error
                    : error && error.message
                    ? error.message
                    : undefined,
              },
            }
          )
        })
    }

    if (action === 'reset') {
      const apiCall = inviteMode
        ? dp.resetPasswordInvite({ token, password: values.password })
        : dp.resetPassword({ token, password: values.password })

      apiCall
        .then(() => {
          setLoading(false)
          setShowRedirectModal(true)
          // Redirect to login page after 2 seconds
          setTimeout(() => {
            window.location.replace('/admin/login')
          }, 2000)
        })
        .catch((error) => {
          setLoading(false)
          const defaultMessage = inviteMode
            ? 'This invitation link has expired or has already been used.'
            : 'ra.auth.sign_in_error'
          notify(
            typeof error === 'string'
              ? error
              : typeof error === 'undefined' || !error.message
              ? defaultMessage
              : error.message,
            {
              type: 'warning',
              messageArgs: {
                _:
                  typeof error === 'string'
                    ? error
                    : error && error.message
                    ? error.message
                    : undefined,
              },
            }
          )
        })
    }
  }

  let buttonText = 'Sign in'

  let resolver: any = undefined

  if (action === 'reset') {
    buttonText = 'Save'
    resolver = yupResolver(schema)
  }
  if (action === 'forgotten') {
    buttonText = 'Request link'
  }

  const titleText = React.useMemo(() => {
    if (action === 'login') return 'Sign in to your account'
    if (inviteMode) return 'Set your password'
    return 'Reset your password'
  }, [action, inviteMode])

  return (
    <>
      <StyledForm
        onSubmit={submit}
        mode="onChange"
        noValidate
        className={className}
        resolver={resolver}
      >
        <section className="space-y-8 w-[350px] mx-4 my-2">
          <div className="flex flex-col items-center space-y-6">
            <div className="flex flex-col items-center space-y-0.5">
              <img src="/logo.png" className="h-10" alt="SpendQube logo" />
              <h2>
                Spend<span className="font-medium">Qube</span>
              </h2>
            </div>
            <h1 className="font-semibold text-xl">{titleText}</h1>
          </div>
          <div>
            {action === 'forgotten' && (
              <div className="space-y-4">
                <button
                  className="w-full flex space-x-3 text-sm font-semibold items-center text-indigo-700 hover:underline active"
                  onClick={() => setAction('login')}
                >
                  <ArrowLeftIcon className="h-3" />
                  <span>Back to Login</span>
                </button>
                <p className="text-slate-700 pb-2 text-sm">
                  Fill in your email address and you will be sent a link to
                  follow to reset your password
                </p>
              </div>
            )}
            {(action === 'login' || action === 'forgotten') && (
              <TextInput
                autoFocus
                source="username"
                label="Email address"
                validate={required()}
                fullWidth
              />
            )}
            {(action === 'login' || action === 'reset') && (
              <TextInput
                source="password"
                label={translate('ra.auth.password')}
                type="password"
                autoComplete="current-password"
                validate={required()}
                fullWidth
              />
            )}

            {action === 'reset' && (
              <TextInput
                source="password2"
                label="Confirm password"
                type="password"
                autoComplete="current-password"
                validate={required()}
                fullWidth
              />
            )}
            <div className="space-y-3">
              <Button
                variant="contained"
                type="submit"
                color="primary"
                disabled={loading}
                fullWidth
                className={LoginFormClasses.button}
              >
                {loading ? (
                  <CircularProgress
                    className={LoginFormClasses.icon}
                    size={19}
                    thickness={3}
                  />
                ) : (
                  buttonText
                )}
              </Button>
              {action == 'login' && (
                <div className="flex justify-end">
                  <a
                    className="text-indigo-700 font-semibold text-sm hover:underline active cursor-pointer"
                    onClick={() => setAction('forgotten')}
                  >
                    Forgotten password?
                  </a>
                </div>
              )}
            </div>
          </div>
        </section>
      </StyledForm>
      <Tagline floating />
      <Dialog open={!!showRedirectModal} onClose={() => {}}>
        <DialogTitle>Thank you</DialogTitle>
        <DialogContent>
          You will now be redirected to the login page
        </DialogContent>
      </Dialog>
      <Dialog open={!!showExpiredModal} onClose={() => {}}>
        <DialogTitle>Link expired</DialogTitle>
        <DialogContent>
          Link expired - we will redirect you to the login page
        </DialogContent>
      </Dialog>
    </>
  )
}

const PREFIX = 'RaLoginForm'

export const LoginFormClasses = {
  content: `${PREFIX}-content`,
  button: `${PREFIX}-button`,
  icon: `${PREFIX}-icon`,
}

const StyledForm = styled(Form, {
  name: PREFIX,
  overridesResolver: (props, styles) => styles.root,
})(({ theme }) => ({
  [`& .${LoginFormClasses.content}`]: {
    width: 300,
  },
  [`& .${LoginFormClasses.button}`]: {
    marginTop: theme.spacing(2),
  },
  [`& .${LoginFormClasses.icon}`]: {
    margin: theme.spacing(0.3),
  },
}))

export interface LoginFormProps {
  redirectTo?: string
  className?: string
}

interface FormData {
  username: string
  password: string
}

LoginForm.propTypes = {
  redirectTo: PropTypes.string,
}
